import { calculateDistance as haversineDistance, getMapLink as buildMapLink, ZIMBABWE_BOUNDS } from '../utils/geo';
import { hashObject, chainHash } from '../utils/integrityChain';

/**
 * Geofencing Service
 * Handles geofencing alerts and notifications based on verifier location
 */

class GeofencingService {
  constructor() {
    this.zones = {
      ARRIVED: 0.1,      // 100m - verifier arrived
      APPROACHING: 1,    // 1km - verifier approaching
      NEARBY: 5,         // 5km - verifier nearby
      FAR: 10           // 10km - verifier far away
    };
    
    this.previousZone = null;
    this.notificationCallbacks = [];
  }

  /**
   * Calculate distance between two coordinates (in km)
   */
  calculateDistance(lat1, lon1, lat2, lon2) {
    return haversineDistance(lat1, lon1, lat2, lon2);
  }

  /**
   * Determine which geofence zone the verifier is in
   */
  getZone(distanceKm) {
    if (distanceKm <= this.zones.ARRIVED) return 'ARRIVED';
    if (distanceKm <= this.zones.APPROACHING) return 'APPROACHING';
    if (distanceKm <= this.zones.NEARBY) return 'NEARBY';
    if (distanceKm <= this.zones.FAR) return 'FAR';
    return 'VERY_FAR';
  }

  /**
   * Check if verifier crossed a geofence boundary
   */
  checkGeofence(verifierLocation, propertyLocation) {
    const distance = this.calculateDistance(
      verifierLocation.lat,
      verifierLocation.lng,
      propertyLocation.lat,
      propertyLocation.lng
    );

    const currentZone = this.getZone(distance);
    const zoneChanged = this.previousZone && this.previousZone !== currentZone;

    const result = {
      distance,
      currentZone,
      previousZone: this.previousZone,
      zoneChanged,
      notification: null
    };

    // Generate notification if zone changed
    if (zoneChanged) {
      result.notification = this.generateNotification(currentZone, distance);
    }

    // Update previous zone
    this.previousZone = currentZone;

    return result;
  }

  /**
   * Generate notification based on zone
   */
  generateNotification(zone, distance) {
    const notifications = {
      ARRIVED: {
        title: '🎯 Verifier Has Arrived!',
        message: 'The verifier is at your property. Verification will begin shortly.',
        severity: 'success',
        priority: 'high',
        sound: true
      },
      APPROACHING: {
        title: '🚗 Verifier Approaching',
        message: `The verifier is ${distance.toFixed(1)}km away and will arrive soon.`,
        severity: 'info',
        priority: 'medium',
        sound: true
      },
      NEARBY: {
        title: '📍 Verifier Nearby',
        message: `The verifier is ${distance.toFixed(1)}km away.`,
        severity: 'info',
        priority: 'low',
        sound: false
      },
      FAR: {
        title: '🚙 Verifier On The Way',
        message: `The verifier is ${distance.toFixed(1)}km away and heading to your property.`,
        severity: 'info',
        priority: 'low',
        sound: false
      }
    };

    return notifications[zone] || null;
  }

  /**
   * Calculate ETA based on distance
   */
  calculateETA(distanceKm, averageSpeedKmh = 30) {
    const hours = distanceKm / averageSpeedKmh;
    const minutes = Math.ceil(hours * 60);
    
    if (minutes < 1) return 'Arriving now';
    if (minutes === 1) return '1 minute';
    if (minutes < 60) return `${minutes} minutes`;
    
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hrs}h ${mins}m`;
  }

  /**
   * Register callback for notifications
   */
  onNotification(callback) {
    this.notificationCallbacks.push(callback);
  }

  /**
   * Trigger notification to all registered callbacks
   */
  triggerNotification(notification) {
    this.notificationCallbacks.forEach(callback => {
      try {
        callback(notification);
      } catch (err) {
        console.error('Notification callback error:', err);
      }
    });

    // Browser notification if permitted
    if (notification.priority === 'high' && 'Notification' in window) {
      if (Notification.permission === 'granted') {
        new Notification(notification.title, {
          body: notification.message,
          icon: '/logo192.png',
          badge: '/logo192.png',
          tag: 'verifier-location',
          requireInteraction: notification.priority === 'high'
        });
      } else if (Notification.permission !== 'denied') {
        Notification.requestPermission();
      }
    }

    // Play sound if specified (uses Web Audio API — no external file needed)
    if (notification.sound && window.AudioContext) {
      try {
        const ctx = new AudioContext();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = 880;
        osc.type = 'sine';
        gain.gain.setValueAtTime(0.3, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.5);
      } catch (err) {
        console.log('Sound notification not available');
      }
    }
  }

  /**
   * Create a tamper-evident geofence proof for offline verification.
   * Each proof is individually hashed and chained to the previous one.
   *
   * @param {Object} verifierLocation - { lat, lng, accuracy, altitude, heading, speed }
   * @param {Object} siteCoordinates  - { lat, lng }
   * @param {Array}  previousProofs   - Previously collected proofs (for anti-spoofing + chaining)
   * @returns {Promise<Object>} The proof object with hash
   */
  async createGeofenceProof(verifierLocation, siteCoordinates, previousProofs = []) {
    const distanceKm = this.calculateDistance(
      verifierLocation.lat, verifierLocation.lng,
      siteCoordinates.lat, siteCoordinates.lng
    );
    const distanceMeters = distanceKm * 1000;
    const zone = this.getZone(distanceKm);

    // Run anti-spoofing checks locally
    const antiSpoofing = this._runAntiSpoofingChecks(verifierLocation, previousProofs);

    const proof = {
      id: `gp_${previousProofs.length}`,
      timestamp: new Date().toISOString(),
      deviceTimestamp: Date.now(),
      location: {
        lat: verifierLocation.lat,
        lng: verifierLocation.lng,
        accuracy: verifierLocation.accuracy || null,
        altitude: verifierLocation.altitude || null,
        heading: verifierLocation.heading || null,
        speed: verifierLocation.speed || null
      },
      siteCoordinates: { lat: siteCoordinates.lat, lng: siteCoordinates.lng },
      distanceMeters: Math.round(distanceMeters * 100) / 100,
      zone,
      antiSpoofing
    };

    // Hash the proof (excluding the hash field)
    proof.hash = await hashObject(proof);

    return proof;
  }

  /**
   * Run client-side anti-spoofing checks against a new location reading
   * @private
   */
  _runAntiSpoofingChecks(location, previousProofs) {
    const MAX_ACCURACY_M = 100;
    const MAX_SPEED_KMH = 200;
    const MAX_JUMP_M = 1000;
    const MIN_TIME_FOR_JUMP_S = 60;

    const result = {
      accuracyCheck: { passed: true, value: location.accuracy },
      speedCheck: { passed: true, speedKmh: null },
      continuityCheck: { passed: true, jumpMeters: null },
      zimbabweBoundsCheck: { passed: true }
    };

    // 1. Accuracy check
    if (location.accuracy && location.accuracy > MAX_ACCURACY_M) {
      result.accuracyCheck.passed = false;
    }

    // 2. Zimbabwe bounds check
    if (ZIMBABWE_BOUNDS) {
      const inBounds = (
        location.lat >= ZIMBABWE_BOUNDS.south &&
        location.lat <= ZIMBABWE_BOUNDS.north &&
        location.lng >= ZIMBABWE_BOUNDS.west &&
        location.lng <= ZIMBABWE_BOUNDS.east
      );
      result.zimbabweBoundsCheck.passed = inBounds;
    }

    // 3. Speed + continuity checks (need at least one previous proof)
    if (previousProofs.length > 0) {
      const prev = previousProofs[previousProofs.length - 1];
      const prevLoc = prev.location;
      const timeDiffS = (Date.now() - prev.deviceTimestamp) / 1000;

      if (prevLoc && timeDiffS > 0) {
        const distKm = this.calculateDistance(
          location.lat, location.lng,
          prevLoc.lat, prevLoc.lng
        );
        const distM = distKm * 1000;
        const speedKmh = (distKm / timeDiffS) * 3600;

        result.speedCheck.speedKmh = Math.round(speedKmh * 10) / 10;
        if (speedKmh > MAX_SPEED_KMH) {
          result.speedCheck.passed = false;
        }

        result.continuityCheck.jumpMeters = Math.round(distM);
        if (distM > MAX_JUMP_M && timeDiffS < MIN_TIME_FOR_JUMP_S) {
          result.continuityCheck.passed = false;
        }
      }
    }

    return result;
  }

  /**
   * Reset geofencing state
   */
  reset() {
    this.previousZone = null;
  }

  /**
   * Get all zone thresholds
   */
  getZones() {
    return { ...this.zones };
  }

  /**
   * Check if geolocation is supported
   * @returns {boolean}
   */
  isSupported() {
    return 'geolocation' in navigator;
  }

  /**
   * Get current position
   * @param {Object} options - Geolocation options
   * @returns {Promise<GeolocationPosition>}
   */
  getCurrentPosition(options = {}) {
    return new Promise((resolve, reject) => {
      if (!this.isSupported()) {
        reject(new Error('Geolocation is not supported'));
        return;
      }

      const defaultOptions = {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
        ...options
      };

      navigator.geolocation.getCurrentPosition(
        (position) => resolve(position),
        (error) => reject(this.getErrorMessage(error)),
        defaultOptions
      );
    });
  }

  /**
   * Verify verifier is at inspection site (for report writing)
   * @param {Object} siteAddress - Site address with coordinates
   * @param {number} allowedRadius - Allowed distance from site in meters (default: 100m)
   * @returns {Promise<Object>} Verification result
   */
  async verifyOnSite(siteAddress, allowedRadius = 100) {
    if (!siteAddress || !siteAddress.latitude || !siteAddress.longitude) {
      throw new Error('Site coordinates are required');
    }

    try {
      const position = await this.getCurrentPosition();
      const { latitude, longitude, accuracy } = position.coords;

      // Calculate distance in meters
      const distanceKm = this.calculateDistance(
        latitude,
        longitude,
        siteAddress.latitude,
        siteAddress.longitude
      );
      const distanceMeters = distanceKm * 1000;

      const isWithin = distanceMeters <= allowedRadius;

      return {
        verified: isWithin,
        distance: Math.round(distanceMeters),
        allowedRadius,
        accuracy: Math.round(accuracy),
        message: isWithin
          ? `You are at the site (${Math.round(distanceMeters)}m away)`
          : `You are ${Math.round(distanceMeters)}m away from the site. Please move within ${allowedRadius}m to enable report writing.`,
        position: {
          latitude,
          longitude,
          accuracy,
          timestamp: position.timestamp
        },
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get location metadata for report
   * @returns {Promise<Object>} Location metadata
   */
  async getLocationMetadata() {
    try {
      const position = await this.getCurrentPosition();
      
      return {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        altitude: position.coords.altitude,
        altitudeAccuracy: position.coords.altitudeAccuracy,
        heading: position.coords.heading,
        speed: position.coords.speed,
        timestamp: position.timestamp,
        capturedAt: new Date().toISOString()
      };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Request location permission
   * @returns {Promise<string>} Permission state
   */
  async requestPermission() {
    if (!this.isSupported()) {
      throw new Error('Geolocation is not supported by this browser');
    }

    try {
      const result = await navigator.permissions.query({ name: 'geolocation' });
      return result.state; // 'granted', 'denied', or 'prompt'
    } catch (error) {
      // Some browsers don't support permissions.query for geolocation
      return new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          () => resolve('granted'),
          (error) => {
            if (error.code === error.PERMISSION_DENIED) {
              resolve('denied');
            } else {
              reject(error);
            }
          }
        );
      });
    }
  }

  /**
   * Get human-readable error message
   * @param {GeolocationPositionError} error
   * @returns {Error}
   */
  getErrorMessage(error) {
    switch (error.code) {
      case error.PERMISSION_DENIED:
        return new Error(
          'Location permission denied. Please enable location access in your browser settings.'
        );
      case error.POSITION_UNAVAILABLE:
        return new Error(
          'Location information is unavailable. Please check your device settings.'
        );
      case error.TIMEOUT:
        return new Error(
          'Location request timed out. Please try again.'
        );
      default:
        return new Error('An unknown error occurred while getting location.');
    }
  }

  /**
   * Format coordinates for display
   * @param {number} lat
   * @param {number} lon
   * @returns {string}
   */
  formatCoordinates(lat, lon) {
    const latDir = lat >= 0 ? 'N' : 'S';
    const lonDir = lon >= 0 ? 'E' : 'W';
    return `${Math.abs(lat).toFixed(6)}°${latDir}, ${Math.abs(lon).toFixed(6)}°${lonDir}`;
  }

  /**
   * Get Google Maps link for coordinates
   * @param {number} lat
   * @param {number} lon
   * @returns {string}
   */
  getMapLink(lat, lon) {
    return buildMapLink(lat, lon);
  }
}

// Export singleton instance
const geofencingService = new GeofencingService();
export default geofencingService;

